import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const LAYERS = [
  { step: '01', label: 'Measure',  color: 'var(--neon-cyan)',   desc: 'Scope 1, 2 & 3 carbon accounting across 24 GHG categories. Automated carbon score. Supply chain engagement.', link: '/emissions', cta: 'Start measuring' },
  { step: '02', label: 'Plan',     color: 'var(--neon-yellow)', desc: 'Science-based roadmap aligned to SBTi 1.5°C. MACC curve. Milestone tracking to net-zero.', link: '/roadmap', cta: 'Build roadmap' },
  { step: '03', label: 'Execute',  color: 'var(--neon-orange)', desc: 'Low-carbon logistics, packaging, waste, and procurement modules. Every action feeds back into your carbon score.', link: '/services/logistics', cta: 'Explore services' },
  { step: '04', label: 'Trade',    color: 'var(--neon-green)',  desc: 'Buy, sell, and retire carbon credits for residual emissions. India CCTS + voluntary markets.', link: '/listings', cta: 'Browse credits' },
  { step: '05', label: 'Report',   color: 'var(--neon-pink)',   desc: 'One-click BRSR, GHG Protocol, GRI, and CBAM-ready ESG reports. Auto-filled from your emission logs.', link: '/reports', cta: 'View reports' },
];

const MODULES = [
  { label: 'Emissions Tracker',   sub: 'Emission Ledger', color: 'var(--neon-cyan)',   link: '/emissions' },
  { label: 'Supply Chain',        sub: 'Supplier Insights', color: 'var(--neon-yellow)', link: '/supply-chain' },
  { label: 'Low-Carbon Logistics',sub: 'Service module',color: 'var(--neon-orange)', link: '/services/logistics' },
  { label: 'Circular Packaging',  sub: 'EPR + DfR',     color: 'var(--neon-pink)',   link: '/services/packaging' },
  { label: 'Waste Management',    sub: 'CPCB EPR',      color: 'var(--neon-green)',  link: '/services/waste' },
  { label: 'Sustainable Procurement', sub: 'Scope 3 Cat. 1',color: 'var(--neon-cyan)',   link: '/services/procurement' },
  { label: 'Roadmap',             sub: 'Net-Zero Planner', color: 'var(--neon-yellow)', link: '/roadmap' },
  { label: 'Vendor Directory',    sub: 'Vendor Hub',    color: 'var(--neon-orange)', link: '/vendors' },
  { label: 'ESG Reports',         sub: 'Compliance Reports', color: 'var(--neon-pink)',   link: '/reports' },
  { label: 'Carbon Credits',      sub: 'Carbon Economy',color: 'var(--neon-green)',  link: '/listings' },
];

export default function Home() {
  const [prices, setPrices] = useState(null);
  const [stats, setStats]   = useState(null);

  useEffect(() => {
    api.get('/market/prices').then((r) => setPrices(r.data)).catch(() => {});
    api.get('/market/stats').then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="page-shell page-stack">

      {/* ── Hero ── */}
      <section className="hero-shell">
        <div className="surface-panel glow-surface hero-panel grid-bg">
          <div>
            <div className="hero-kicker">Carbon Operations Platform</div>
            <h1 className="hero-heading">
              Measure. Plan. Execute.
              <span className="accent"> Reach net-zero.</span>
            </h1>
            <p className="hero-copy">
              CarbonX is a full-stack DaaS platform — from Scope 1/2/3 carbon accounting and supplier engagement, to science-based roadmaps, green service modules, and BRSR-aligned ESG reporting. Trade residual credits when you're ready.
            </p>
            <div className="hero-actions">
              <Link to="/signup" className="btn btn-primary">Get started free</Link>
              <Link to="/emissions" className="btn btn-secondary">Measure emissions</Link>
              <Link to="/vendors" className="btn btn-secondary">Browse vendors</Link>
            </div>
            <div className="hero-note">
              <div className="info-chip">GHG Protocol · SBTi · BRSR · CPCB EPR · India CCTS</div>
              <div className="info-chip">10 modules — measure to offset in one platform</div>
            </div>
          </div>

          <div className="duo-grid">
            {[
              { label: 'Platform modules', value: '10', sub: 'Measure → Plan → Execute → Trade → Report', color: 'var(--neon-green)' },
              { label: 'CCC Reference', value: prices ? `₹${prices.spot.ccc_mandatory.toLocaleString('en-IN')}` : '₹2,840', sub: 'India mandatory carbon credit price', color: 'var(--neon-cyan)' },
            ].map((s) => (
              <div key={s.label} className="stat-card card" style={{ padding: '1.1rem', background: 'rgba(255,255,255,0.03)' }}>
                <div className="stat-label">{s.label}</div>
                <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
                <div className="stat-sub">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Platform spotlight cards */}
        <div className="hero-side-grid">
          {[
            { kicker: 'Carbon Score', value: '0–100', copy: 'Automated carbon score updated every time you log an activity. Grade A–D with rule-based improvement recommendations.', color: 'var(--neon-green)' },
            { kicker: 'BRSR Reports', value: 'Auto', copy: 'Principle 6 disclosures auto-filled from emission logs. Data gaps highlighted with direct links to the right module.', color: 'var(--neon-cyan)' },
            { kicker: 'EPR Compliance', value: 'Live', copy: 'India CPCB EPR liability tracked in real time from packaging and waste modules. Shortfall alerts before deadlines.', color: 'var(--neon-orange)' },
          ].map((card) => (
            <div key={card.kicker} className="surface-panel spotlight-card">
              <div className="spotlight-label">{card.kicker}</div>
              <div className="spotlight-value" style={{ color: card.color }}>{card.value}</div>
              <div className="spotlight-copy">{card.copy}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Platform stats ── */}
      <section className="stats-grid">
        {[
          { label: 'GHG categories', value: '24', sub: 'Scope 1, 2 & 3 per GHG Protocol', color: 'var(--neon-cyan)' },
          { label: 'Emission factors', value: '28+', sub: 'India-specific & GHG Protocol aligned', color: 'var(--neon-orange)' },
          { label: 'MACC interventions', value: '7', sub: 'Cost-ranked abatement curve', color: 'var(--neon-yellow)' },
          { label: 'Credits traded', value: stats ? `${(stats.total_credits_traded / 1000).toFixed(1)}K` : '142K', sub: 'tCO2e across marketplace', color: 'var(--neon-green)' },
        ].map((s) => (
          <div key={s.label} className="surface-panel stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </section>

      {/* ── 5-layer OS ── */}
      <section className="page-stack">
        <div className="section-header-row">
          <div className="section-stack">
            <div className="section-tag">How CarbonX works</div>
            <h2 className="section-title">Five layers. One decarbonization OS.</h2>
            <p className="section-copy">Every layer feeds the next — emissions data powers the roadmap, service modules reduce the score, and residual credits are offset through the carbon economy.</p>
          </div>
        </div>
        <div className="feature-grid">
          {LAYERS.map((l) => (
            <div key={l.step} className="surface-panel feature-card">
              <div className="feature-icon" style={{ color: l.color }}>{l.step}</div>
              <div className="feature-title">{l.label}</div>
              <p className="feature-copy">{l.desc}</p>
              <Link to={l.link} className="feature-link" style={{ color: l.color }}>{l.cta}</Link>
            </div>
          ))}
        </div>
      </section>

      {/* ── Module grid ── */}
      <section className="page-stack">
        <div className="section-stack" style={{ marginBottom: '1.25rem' }}>
          <div className="section-tag">Platform modules</div>
          <h2 className="section-title">Everything in one OS</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
          {MODULES.map((m) => (
            <Link key={m.label} to={m.link} style={{ textDecoration: 'none' }}>
              <div className="surface-panel" style={{ padding: '1.1rem 1.25rem', cursor: 'pointer', borderLeft: `3px solid ${m.color}` }}>
                <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.2rem' }}>{m.label}</div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: m.color }}>{m.sub}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── India context ── */}
      <section className="section-grid-2">
        <div className="surface-panel" style={{ padding: '1.6rem' }}>
          <div className="section-tag" style={{ color: 'var(--neon-orange)' }}>India regulatory alignment</div>
          <h2 className="section-title" style={{ marginTop: '1rem' }}>Built for India's decarbonization mandates</h2>
          <p className="section-copy" style={{ marginTop: '1rem' }}>
            CarbonX aligns with CCTS (BEE/MoEFCC), SEBI BRSR Principle 6, CPCB EPR rules for plastics, and India's Net Zero 2070 pathway. Every module speaks the same regulatory language.
          </p>
          <div className="hero-note" style={{ marginTop: '1.6rem' }}>
            <div className="info-chip">CCTS · BEE · MoEFCC</div>
            <div className="info-chip">SEBI BRSR Principle 6</div>
            <div className="info-chip">CPCB EPR plastic rules</div>
            <div className="info-chip">GHG Protocol · SBTi 1.5°C</div>
          </div>
          <div className="inline-actions" style={{ marginTop: '1.6rem' }}>
            <Link to="/market/india" className="btn btn-orange">India carbon market</Link>
            <Link to="/reports" className="btn btn-secondary">BRSR reports</Link>
          </div>
        </div>

        <div className="duo-grid">
          {[
            { label: 'Covered sectors', value: '9', color: 'var(--neon-orange)' },
            { label: 'Net zero target', value: '2070', color: 'var(--neon-green)' },
            { label: 'ESG frameworks', value: '4', color: 'var(--neon-cyan)' },
            { label: 'EPR modules', value: '2', color: 'var(--neon-pink)' },
          ].map((s) => (
            <div key={s.label} className="surface-panel stat-card">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color, fontSize: '1.7rem' }}>{s.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="surface-panel glow-surface" style={{ padding: '1.8rem' }}>
        <div className="section-grid-2">
          <div className="section-stack">
            <div className="section-tag">Get started</div>
            <h2 className="section-title">Start your decarbonization journey today</h2>
            <p className="section-copy">
              Create a free account, log your first emission activity, and get your carbon score in under 5 minutes. No complex setup — the platform guides you through each layer.
            </p>
          </div>
          <div className="surface-panel" style={{ padding: '1.2rem', background: 'var(--glass-bg)' }}>
            <div className="stat-label">What happens next</div>
            <div style={{ display: 'grid', gap: '0.6rem', marginTop: '0.75rem' }}>
              {['Log your first Scope 1/2/3 emission activity', 'Get your automated carbon score + grade', 'Identify hotspots and get reduction recommendations', 'Build a science-based roadmap to net-zero'].map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--neon-cyan)', minWidth: '1.2rem' }}>0{i + 1}</span>
                  <span style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '0.82rem', color: 'var(--text)' }}>{step}</span>
                </div>
              ))}
            </div>
            <div className="inline-actions" style={{ marginTop: '1.2rem' }}>
              <Link to="/signup" className="btn btn-primary">Create free account</Link>
              <Link to="/login" className="btn btn-secondary">Login</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
